import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import { useDispatch, useSelector } from 'react-redux';
import { BookingFilterAny } from 'stores/rematch/models/types/booking-filter';
import { IRootDispatch, IRootState } from 'stores/rematch/root-store';
import { TimezoneSelectorMode } from 'utilities/enum-utils';
import { usePortalUser } from 'utilities/react-hooks/use-portal-user';
import {
  BookingDrawerContentType,
  bookingsViewContextInitialState,
  BookingsViewContextType,
  OnSelectRosterItemOptions,
  RosterPeriodView,
} from './types';
import { useFetchBookings } from './use-fetch-bookings';
import { useDisplayTimezone } from './use-display-timezone';
import { useDisclosure } from '@good/ui/hooks';
import CreateNewBookingModal from 'views/bookings/listings/components/CreateNewBookingModal';
import { useHistory } from 'react-router-dom';
import { Box, Drawer } from '@good/ui/core';
import { IRosterBooking, IRosterShift } from '../bookings-roster-view/types';
import { BookingDrawerContent } from './booking-drawer-content';
import { BookingsViewType } from '../booking-view-toolbar/types';
import { ROSTER_TAB } from '../bookings-roster-view/components/roster-common-enums';
import { Updater } from '@tanstack/react-table';

export const BookingsViewContext = createContext<BookingsViewContextType>(bookingsViewContextInitialState);

interface BookingsViewProviderProps {
  children: React.ReactNode;
  view: BookingsViewType;
  onChangeView: (view: BookingsViewType) => void;
}

export const BookingsViewProvider = ({ children, view, onChangeView }: BookingsViewProviderProps) => {
  const history = useHistory();
  const [rosterPeriod, setRosterPeriod] = useState<RosterPeriodView>('weekly');
  const { bookingsStore } = useDispatch<IRootDispatch>();
  const portalUser = usePortalUser();
  const bookingDisplayTzMode = useSelector((state: IRootState) => state.bookingsStore.bookingDisplayTzMode);
  const doFetchBookingViews = bookingsStore.doFetchBookingViews;
  const [displayTimezone, setDisplayTimezone] = useDisplayTimezone();
  const [hasFetchedBookingViews, setHasFetchedBookingViews] = useState(false);
  const [createBookingOpened, { open: openCreateBooking, close: closeCreateBooking }] = useDisclosure(false);
  const [selectedCustomer, setSelectedCustomer] = useState<unknown | undefined>(undefined);
  const [selectedCreateDate, setSelectedCreateDate] = useState<Date | undefined>(undefined);
  const sideMenuCollapsed = useSelector((state: IRootState) => state.navigationStore.sideMenuCollapsed);
  const [drawerOpened, setDrawerOpened] = useState<boolean>(false);
  const [canCloseDrawer, setCanCloseDrawer] = useState<boolean>(true);
  const [selectedRosterItem, setSelectedRosterItem] = useState<IRosterBooking | IRosterShift | null>(null);
  const [selectedRosterItemType, setSelectedRosterItemType] = useState<BookingDrawerContentType | null>(null);
  const [selectedRosterTab, setSelectedRosterTab] = useState<ROSTER_TAB | null>(null);
  const refetchRoster = useRef<(() => Promise<void>) | null>(null);

  const registerRefetchRoster = useCallback((refetch: () => Promise<void>) => {
    refetchRoster.current = refetch;
  }, []);

  const toggleDrawer = useCallback(
    (nextOpenState: boolean) => {
      if (nextOpenState) {
        setDrawerOpened(nextOpenState);
      } else if (!nextOpenState && canCloseDrawer) {
        setDrawerOpened(nextOpenState);
      }
    },
    [canCloseDrawer],
  );

  const openDrawer = useCallback(() => toggleDrawer(true), [toggleDrawer]);
  const closeDrawer = useCallback(() => toggleDrawer(false), [toggleDrawer]);

  const onDeselectRosterItem = useCallback(() => {
    setSelectedRosterItem(null);
    setSelectedRosterItemType(null);
    setSelectedRosterTab(null);
  }, []);

  const onSelectRosterItem = useCallback(
    (next: Updater<OnSelectRosterItemOptions> | OnSelectRosterItemOptions) => {
      if (typeof next === 'function') {
        let prevItem: IRosterBooking | IRosterShift | null = null;
        let prevType: BookingDrawerContentType | null = null;
        let prevTab: ROSTER_TAB | null = null;
        setSelectedRosterItem((prev) => {
          prevItem = prev;
          setSelectedRosterItemType((prevTypeState) => {
            prevType = prevTypeState;
            setSelectedRosterTab((prevTabState) => {
              prevTab = prevTabState;
              const { tab } = next({ item: prevItem, type: prevType, tab: prevTab });
              return tab ?? prevTab;
            });
            const { type } = next({ item: prevItem, type: prevType, tab: prevTab });
            return type ?? prevType;
          });
          const { item } = next({ item: prevItem, type: prevType, tab: prevTab });
          return item ?? prevItem;
        });
        openDrawer();
        return;
      }

      const { item, type, tab } = next;
      setSelectedRosterItem(item);
      setSelectedRosterItemType(type);
      setSelectedRosterTab(tab);
      openDrawer();
    },
    [openDrawer],
  );

  const onOpenCreateBooking = useCallback(
    (opts?: {
      customer?: unknown;
      startDate?: Date;
    }) => {
      openCreateBooking();
      setSelectedCustomer(opts?.customer ?? undefined);
      setSelectedCreateDate(opts?.startDate ?? undefined);
    },
    [openCreateBooking],
  );

  const onCloseCreateBooking = useCallback(() => {
    closeCreateBooking();
    setSelectedCustomer(undefined);
    setSelectedCreateDate(undefined);
  }, [closeCreateBooking]);

  const {
    fetchBookings,
    hasFetchedInitialViews,
    fetchedViewIds,
    hasError,
    showActionSheet,
    indeterminateCheck,
    checkAllIndicator,
    pageNumbers,
    pageTimestamps,
    resetViews,
    pageSize,
    onChangeShowActionSheet,
    onChangeIndeterminateCheck,
    onChangeCheckAllIndicator,
  } = useFetchBookings();

  useEffect(() => {
    (async () => {
      await doFetchBookingViews();
      setHasFetchedBookingViews(true);
    })();
  }, [doFetchBookingViews]);

  useEffect(() => {
    (async () => {
      // if the booking views have not been fetched, you will get two requests
      if (hasFetchedBookingViews) {
        await fetchBookings({ initialFetch: true, caller: 'BookingsViewProvider.useEffect (initial)' });
      }
    })();
  }, [fetchBookings, hasFetchedBookingViews]);

  const onChangeTimezone = useCallback(
    async (type: 'mode' | 'customTimezone', value: TimezoneSelectorMode) => {
      if (type === 'mode') {
        await bookingsStore.setBookingDisplayTzMode(value);
        setDisplayTimezone((prev) =>
          value === TimezoneSelectorMode.MyTimezone
            ? portalUser?.timezone
            : value === TimezoneSelectorMode.BookingTimezone
              ? null
              : bookingDisplayTzMode
                ? bookingDisplayTzMode
                : prev,
        );
      } else if (type === 'customTimezone') {
        await bookingsStore.setBookingDisplayTzCustom(value);
        setDisplayTimezone(value);
      }
    },
    [
      bookingDisplayTzMode,
      portalUser?.timezone,
      bookingsStore.setBookingDisplayTzMode,
      bookingsStore.setBookingDisplayTzCustom,
      setDisplayTimezone,
    ],
  );

  const onChangeFilter = useCallback(
    (filters: BookingFilterAny[]) => {
      bookingsStore.setHasBookingListingFilterChanged(true);
      bookingsStore.setBookingsFilter(filters);
      fetchBookings({ filters, reset: true, caller: 'BookingsViewProvider.onChangeFilter' });
    },
    [bookingsStore.setHasBookingListingFilterChanged, bookingsStore.setBookingsFilter, fetchBookings],
  );

  const retry = useCallback(async () => {
    onChangeCheckAllIndicator(false);
    onChangeIndeterminateCheck(false);
    onChangeShowActionSheet(false);

    await fetchBookings({
      allViews: true,
      reset: true,
      initialFetch: true,
      caller: 'retry',
    });
  }, [fetchBookings, onChangeCheckAllIndicator, onChangeIndeterminateCheck, onChangeShowActionSheet]);

  const refreshListings = useCallback(async () => {
    onChangeCheckAllIndicator(false);
    onChangeIndeterminateCheck(false);
    onChangeShowActionSheet(false);

    await fetchBookings({
      allViews: true,
      reset: true,
      caller: 'BookingsViewProvider.refreshListings',
    });

    if (view === 'calendar') {
      refetchRoster.current?.();
    }
  }, [fetchBookings, onChangeCheckAllIndicator, onChangeIndeterminateCheck, onChangeShowActionSheet, view]);

  const onChangeRosterPeriod = useCallback((period: RosterPeriodView) => {
    setRosterPeriod(period);
  }, []);

  const drawerWidth = '25rem';

  const pageWidth = useMemo(() => {
    if (!drawerOpened) {
      return '100%';
    }

    if (!sideMenuCollapsed) {
      return `calc(100vw - ${drawerWidth} - var(--app-shell-navbar-width))`;
    }

    return `calc(100vw - ${drawerWidth})`;
  }, [sideMenuCollapsed, drawerOpened]);

  const value: BookingsViewContextType = useMemo(
    () => ({
      checkAllIndicator,
      displayTimezone,
      fetchBookings,
      fetchedViewIds,
      hasError,
      hasFetchedInitialViews,
      indeterminateCheck,
      onChangeCheckAllIndicator,
      onChangeFilter,
      onChangeIndeterminateCheck,
      onChangeRosterPeriod,
      onChangeShowActionSheet,
      onChangeTimezone,
      pageNumbers,
      pageSize,
      pageTimestamps,
      refreshListings,
      resetViews,
      retry,
      rosterPeriod,
      showActionSheet,
      onOpenCreateBooking,
      onSelectRosterItem,
      selectedRosterItem,
      registerRefetchRoster,
      onDeselectRosterItem,
    }),
    [
      checkAllIndicator,
      displayTimezone,
      fetchBookings,
      fetchedViewIds,
      hasError,
      hasFetchedInitialViews,
      indeterminateCheck,
      onChangeCheckAllIndicator,
      onChangeFilter,
      onChangeIndeterminateCheck,
      onChangeRosterPeriod,
      onChangeShowActionSheet,
      onChangeTimezone,
      pageNumbers,
      pageSize,
      pageTimestamps,
      refreshListings,
      resetViews,
      retry,
      rosterPeriod,
      showActionSheet,
      onOpenCreateBooking,
      onSelectRosterItem,
      selectedRosterItem,
      registerRefetchRoster,
      onDeselectRosterItem,
    ],
  );

  return (
    <BookingsViewContext.Provider value={value}>
      <Box
        w={pageWidth}
        style={{
          transition: 'width 0.3s ease-in-out',
        }}
      >
        {children}
      </Box>
      <CreateNewBookingModal
        isOpen={createBookingOpened}
        history={history}
        closeCreateBookingModal={onCloseCreateBooking}
        incomingCustomer={selectedCustomer}
        incomingStartDateTime={selectedCreateDate}
      />
      <Drawer.Root
        opened={drawerOpened}
        onClose={closeDrawer}
        lockScroll={false}
        position='right'
        shadow='none'
        closeOnClickOutside
        padding={0}
        size={drawerWidth}
      >
        <Drawer.Content>
          <Drawer.Body
            h='100%'
            display='flex'
            style={{ flexDirection: 'column', borderLeft: '1px solid var(--mantine-color-gray-4)' }}
          >
            <Box pos='sticky' top={0} p='md' bg='rgba(252, 252, 252, 1)' w='100%' style={{ zIndex: 10 }}>
              <Drawer.CloseButton />
            </Box>
            <BookingDrawerContent
              selectedRosterItem={selectedRosterItem}
              setCanCloseDrawer={setCanCloseDrawer}
              refreshAndCloseData={() => {
                setDrawerOpened(false);
                refetchRoster.current?.();
              }}
              type={selectedRosterItemType}
              rosterTab={selectedRosterTab}
            />
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Root>
    </BookingsViewContext.Provider>
  );
};

type Selector<T, Context> = (value: Context) => T;

export const useBookings = <T,>(selector: Selector<T, BookingsViewContextType>) =>
  useContextSelector(BookingsViewContext, selector);
